import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponse } from '@core/interface/api-response.interface';
import { PageableResponse } from '@core/interface/pageable.interface';
import { RefId } from '@core/interface/ref-id.interface';
import { UrlBuilder } from '@core/model/url-builder';
import { OrganizationStoreResponseDto } from '@domain/organization-store/dtos/organization-store.response.dto';
import { CreateOrganizationStoreRequestDto } from '@domain/organization-store/dtos/create-organization-store.request.dto';
import { ListOrganizationStoreRequestDto } from '@domain/organization-store/dtos/list-organization-store.request.dto';
import { UpdateOrganizationStoreRequestDto } from '@domain/organization-store/dtos/update-organization-store.request.dto';
import { UpdateStateOrganizationStoreRequestDto } from '@domain/organization-store/dtos/update-state-organization-store.request.dto';
import { GetOrganizationStoreRequestDto } from '@domain/organization-store/dtos/get-organization-store.request.dto';
import { AssignPersonToOrganizationStoreRequestDto } from '@domain/organization-store/dtos/assign-person-to-organization-store.request.dto';
import { UnassignPersonFromOrganizationStoreRequestDto } from '@domain/organization-store/dtos/unassign-person-from-organization-store.request.dto';
import { GeneratePinOrganizationStoreRequestDto } from '@domain/organization-store/dtos/generate-pin-organization-store.request.dto';
import { ListAssignedPersonOrganizationStoreRequestDto } from '@domain/organization-store/dtos/list-assigned-person-organization-store.request.dto';
import { UserPersonResponseDto } from '@domain/user-person/dtos/user-person.response.dto';
import { AssignCollectorMerchantRequestDto } from '@domain/organization-store/dtos/assign-collector-merchant.request.dto';

@Injectable({
  providedIn: 'root',
})
export class OrganizationStoreService extends UrlBuilder {
  constructor(private http: HttpClient) {
    super('organization/store');
  }

  create(request: CreateOrganizationStoreRequestDto) {
    const queryUrl = `${this.getApiUrl()}`;
    return this.http.post<ApiResponse<OrganizationStoreResponseDto>>(
      queryUrl,
      request,
    );
  }

  update(request: UpdateOrganizationStoreRequestDto) {
    const { refId } = request;
    const queryUrl = `${this.getApiUrl()}/${refId}`;
    return this.http.put<ApiResponse<OrganizationStoreResponseDto>>(
      queryUrl,
      request,
    );
  }

  updateState(request: UpdateStateOrganizationStoreRequestDto) {
    const { refId } = request;
    const queryUrl = `${this.getApiUrl()}/${refId}/state`;
    return this.http.patch<ApiResponse<OrganizationStoreResponseDto>>(
      queryUrl,
      request,
    );
  }

  generatePin(request: GeneratePinOrganizationStoreRequestDto) {
    const { refId } = request;
    const queryUrl = `${this.getApiUrl()}/${refId}/generate/pin`;
    return this.http.patch<ApiResponse<OrganizationStoreResponseDto>>(
      queryUrl,
      request,
    );
  }

  show(request: RefId) {
    const { refId } = request;
    const queryUrl = `${this.getApiUrl()}/show/${refId}`;
    return this.http.get<ApiResponse<OrganizationStoreResponseDto>>(
      queryUrl,
      {},
    );
  }

  list(request: ListOrganizationStoreRequestDto) {
    const queryUrl = `${this.getApiUrl()}/list`;
    return this.http.get<
      ApiResponse<PageableResponse<OrganizationStoreResponseDto>>
    >(queryUrl, { params: { ...request } });
  }

  get(request: GetOrganizationStoreRequestDto) {
    const queryUrl = `${this.getApiUrl()}/get`;
    return this.http.get<ApiResponse<OrganizationStoreResponseDto[]>>(
      queryUrl,
      { params: { ...request } },
    );
  }

  assignPerson(request: AssignPersonToOrganizationStoreRequestDto) {
    const { refId } = request;
    const queryUrl = `${this.getApiUrl()}/${refId}/assign/person`;
    return this.http.patch<ApiResponse<OrganizationStoreResponseDto>>(
      queryUrl,
      request,
    );
  }

  unassignPerson(request: UnassignPersonFromOrganizationStoreRequestDto) {
    const { refId } = request;
    const queryUrl = `${this.getApiUrl()}/${refId}/unassign/person`;
    return this.http.patch<ApiResponse<OrganizationStoreResponseDto>>(
      queryUrl,
      request,
    );
  }

  listAssignedPerson(request: ListAssignedPersonOrganizationStoreRequestDto) {
    const queryUrl = `${this.getApiUrl()}/list/assigned/person`;
    return this.http.get<ApiResponse<PageableResponse<UserPersonResponseDto>>>(
      queryUrl,
      {
        params: { ...request },
      },
    );
  }

  assignCollectorMerchant(request: AssignCollectorMerchantRequestDto) {
    const queryUrl = `${this.getApiUrl()}/assign/collector-merchant`;
    return this.http.post<ApiResponse<void>>(queryUrl, request);
  }
}
